import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { SocialLoginButton, SocialProvider } from '@inflearn/app-shell';
import { Box, Button, Flex, Title } from '@inflearn/ds-react';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import type { ClientReferrerRequest } from '../../../../resources/@types/accountSchema';
import { ApiError } from '../../../../resources/ApiError';
import { logger } from '../../../../resources/Logger';
import { useCaptcha } from '../../../hooks/useCaptcha';
import { useIsWebview } from '../../../hooks/useIsWebview';
import { useReferUrlNavigation } from '../../../hooks/useReferUrlNavigation';
import { useWebviewThemeParameter } from '../../../lib/useThemeParamter';
import { messageToWebview } from '../../../lib/webview';
import useClientAuthSignupCreateMutation from '../../../mutations/useClientAuthSignupCreateMutation';
import { eventNames } from '../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../utils/GTM/useTrackMixpanelEvent';
import { getClientUserRefererObject } from '../../../utils/refererObject';
import EmailPasswordValidator from '../../../validators/EmailPasswordValidator';
import { ERROR_MESSAGE } from '../../../validators/constants';
import { SocialLoginButtonForWebview } from '../../common/SocialLoginButtonForWebview';
import { getSavedInstructorEmail } from '../../util/getSavedInstructorEmail';
import { ResponseStatus } from '../my/orders/schema';
import { EmailFormItem, type EmailFormValue } from '../signin/find/password/EmailFormItem';
import {
  type PasswordFormValue,
  PasswordVerifyFormItems,
} from '../signin/update/password/PasswordVerifyFormItems';
import {
  AllowedMarketingCheckBox,
  type AllowedMarketingFormValue,
} from './AllowedMarketingCheckBox';
import { CompleteSignup } from './CompleteSignup';
import { SignupDescription } from './SignupDescription';
import SocialLoginDivider from './SocialLoginDivider';
import { TextSlider } from './TextSlider';

type SignupForm = EmailFormValue & PasswordFormValue & AllowedMarketingFormValue;

const INITIAL_VALUES: SignupForm = {
  email: '',
  password: '',
  passwordConfirm: '',
  allowedMarketing: true,
} as const;

const SOCIAL_LOGIN_BUTTONS = [SocialProvider.Kakao, SocialProvider.Google];

export const SignupPageItem = () => {
  const { t } = useTranslation();
  const [isSuccess, setIsSuccess] = useState(false);
  const isWebview = useIsWebview();
  const savedInstructorEmail = getSavedInstructorEmail();
  const form = useForm<SignupForm>({
    mode: 'all',
    resolver: classValidatorResolver(EmailPasswordValidator),
    defaultValues: {
      ...INITIAL_VALUES,
      email: savedInstructorEmail ?? INITIAL_VALUES.email,
    },
  });
  const { trackMixpanelEvent } = useTrackMixpanelEvent();
  const { isDarkTheme } = useWebviewThemeParameter();
  const { getReferUrl } = useReferUrlNavigation();
  const captcha = useCaptcha();

  const { mutate: signupMutate, isLoading } = useClientAuthSignupCreateMutation({
    onSuccess: () => {
      trackMixpanelEvent(eventNames.VIEW_SIGNUP_MAIL_RECEIVED);

      if (isWebview) {
        messageToWebview('sentVerifyingEmail');
      }
      setIsSuccess(true);
    },
    onError: (error) => {
      captcha.resetCaptchaToken();

      if (ApiError.is(error, ResponseStatus.CONFLICT)) {
        form.setError('email', {
          type: 'manual',
          message: ERROR_MESSAGE.EXISTED_EMAIL_REQUEST(),
        });

        return;
      }

      if (ApiError.is(error, ResponseStatus.UNAUTHORIZED)) {
        alert(ERROR_MESSAGE.INVALID_CAPTCHA_TOKEN());

        logger.error(error);

        return;
      }

      form.setError('email', {
        type: 'manual',
        message: ERROR_MESSAGE.TRY_AGAIN(),
      });

      logger.error(error);
    },
  });
  const [isSignupButtonLoading, setIsSignupButtonLoading] = useState(isLoading);

  const handleFormSubmit: SubmitHandler<SignupForm> = ({ email, password, allowedMarketing }) => {
    setIsSignupButtonLoading(!captcha.isCaptchaTokenExist);

    captcha.runWhenCaptchaExist((captchaToken) => {
      setIsSignupButtonLoading(false);

      signupMutate({
        email,
        password,
        allowedMarketing,
        clientReferrer: {
          ...getClientUserRefererObject(),
          documentReferrer: document.referrer,
          referrer: getReferUrl(),
        } as unknown as ClientReferrerRequest,
        captchaToken,
      });
    });
  };

  const socialLoginButtons = useMemo(
    () => (isWebview ? [...SOCIAL_LOGIN_BUTTONS, SocialProvider.Apple] : SOCIAL_LOGIN_BUTTONS),
    [isWebview],
  );

  const delegateLoginProcess = (provider: SocialProvider) => {
    messageToWebview('socialSignUp', { provider: provider.toLowerCase() });
  };

  useEffect(() => {
    trackMixpanelEvent(eventNames.VIEW_SIGNUP);
  }, [trackMixpanelEvent]);

  useEffect(() => {
    setIsSignupButtonLoading(isLoading);
  }, [isLoading]);

  if (isSuccess) {
    return <CompleteSignup />;
  }

  return (
    <>
      <Flex w="100%" direction="column" justify="center" align="center" mb={24} gap={4}>
        <captcha.DevTools />
        {!isWebview && <Title order={2}>{t('회원가입')}</Title>}
        <TextSlider />
      </Flex>
      <Flex
        component="form"
        direction="column"
        gap={16}
        w="100%"
        onSubmit={form.handleSubmit(handleFormSubmit)}
      >
        <EmailFormItem size="lg" register={form.register} errors={form.formState.errors} />
        <PasswordVerifyFormItems
          size="lg"
          register={form.register}
          errors={form.formState.errors}
        />
        <Box ref={captcha.captchaRef} className={captcha.captchaClassName} mx="auto" />
        <Button
          type="submit"
          variant="filled"
          color={isDarkTheme ? darkThemeStyle.signupButton.color : 'infgreen'}
          size="lg"
          fullWidth
          loading={isSignupButtonLoading}
        >
          {t('가입하기')}
        </Button>
      </Flex>
      <Flex direction="column" justify="flex-start" align="center" gap={8} mt={8} mb={32}>
        <SignupDescription />
        <AllowedMarketingCheckBox register={form.register} />
      </Flex>
      <Flex direction="column" align="center" gap="xl" w="100%">
        <SocialLoginDivider>{t('간편 회원가입')}</SocialLoginDivider>
        <Flex pos="relative" component={isWebview ? 'div' : 'ul'} gap={isWebview ? 16 : 8}>
          {socialLoginButtons.map((socialProvider) =>
            isWebview ? (
              <SocialLoginButtonForWebview
                key={socialProvider}
                provider={socialProvider}
                onClick={() => delegateLoginProcess(socialProvider)}
              />
            ) : (
              <li key={socialProvider}>
                <SocialLoginButton provider={socialProvider} showTooltip={false} isAuthPage />
              </li>
            ),
          )}
        </Flex>
      </Flex>
    </>
  );
};

export const darkThemeStyle = {
  signupButton: {
    color: 'infgreen.7',
  },
};
